import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Link2,
  LayoutGrid,
  Printer,
  RefreshCw,
  RotateCcw,
  Sparkles,
  Trash2,
  ArrowLeft,
} from 'lucide-react';
import ScheduleDayBody from './ScheduleDayBody';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 20000;

// The business day is Kuwait time — using the device's local date would show the
// wrong day for anyone whose phone sits in a different timezone.
const todayISO = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kuwait',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

const StaffDailySchedule = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const date = searchParams.get('date') || todayISO();
  const token = localStorage.getItem('sessionToken');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [staffList, setStaffList] = useState([]);
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newIds, setNewIds] = useState([]);
  const [stale, setStale] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState('');
  const [copied, setCopied] = useState(false);
  const knownIds = useRef(null);
  const dayRef = useRef(null);

  const setDate = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set('date', next);
    setSearchParams(params, { replace: true });
  };

  const shiftDate = (days) => {
    const d = new Date(`${date}T00:00:00`);
    d.setDate(d.getDate() + days);
    setDate(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    );
  };

  const fetchStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/provider/daily-schedule/staff`, { headers, params: { date } });
      setStaffList(res.data.staff || []);
      if (!staffId && res.data.staff?.length) {
        navigate(`/provider/schedule/list/${res.data.staff[0].id}?date=${date}`, { replace: true });
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load staff list');
    }
  }, [headers, date, staffId, navigate]);

  const fetchDay = useCallback(async (silent = false) => {
    if (!staffId) return;
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/provider/staff/${staffId}/daily-schedule`, {
        headers,
        params: { date },
      });
      const ids = (res.data.appointments || []).map((a) => a.id);
      if (knownIds.current) {
        const fresh = ids.filter((id) => !knownIds.current.includes(id));
        if (fresh.length) {
          setNewIds((prev) => [...new Set([...prev, ...fresh])]);
          setTimeout(() => setNewIds((prev) => prev.filter((id) => !fresh.includes(id))), 60000);
        }
      }
      knownIds.current = ids;
      dayRef.current = res.data;
      setDay(res.data);
      setLastUpdated(new Date());
      setError('');
      setStale(false);
    } catch (e) {
      // A failed background poll must not wipe a schedule that is already on screen
      if (silent && dayRef.current) {
        setStale(true);
      } else {
        setError(e.response?.data?.detail || t('schedule.loadFailed', 'Could not load the schedule'));
      }
    } finally {
      setLoading(false);
    }
  }, [headers, staffId, date]);

  const fetchShareLink = useCallback(async () => {
    if (!staffId) return;
    try {
      const res = await axios.get(`${API}/provider/staff/${staffId}/schedule-link`, { headers });
      setShareLink(res.data.link);
    } catch {
      setShareLink(null);
    }
  }, [headers, staffId]);

  const createShareLink = async (rotate = false) => {
    if (rotate && !window.confirm('Replace the link? The one your staff member already has will stop working.')) return;
    setShareBusy(true);
    setShareError('');
    try {
      const res = await axios.post(`${API}/provider/staff/${staffId}/schedule-link`, {}, { headers });
      setShareLink(res.data.link);
    } catch (e) {
      setShareError(e.response?.data?.detail || 'Could not create the link. Please try again.');
    } finally {
      setShareBusy(false);
    }
  };

  const revokeShareLink = async () => {
    if (!window.confirm('Turn off this link? Your staff member will no longer be able to open it.')) return;
    setShareBusy(true);
    setShareError('');
    try {
      await axios.delete(`${API}/provider/staff/${staffId}/schedule-link`, { headers });
      setShareLink(null);
    } catch (e) {
      setShareError(e.response?.data?.detail || 'Could not turn off the link. Please try again.');
    } finally {
      setShareBusy(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareLink) return;
    const url = shareLink.url?.startsWith('http') ? shareLink.url : `${window.location.origin}${shareLink.path}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  useEffect(() => {
    knownIds.current = null;
    dayRef.current = null;
    setNewIds([]);
    fetchDay();
  }, [fetchDay]);

  useEffect(() => {
    setShareOpen(false);
    setShareLink(null);
    fetchShareLink();
  }, [fetchShareLink]);

  // Keep the day fresh while it is open — a booking can land at any moment
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') {
        fetchDay(true);
        fetchStaff();
      }
    };
    const id = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [fetchDay, fetchStaff]);

  const shareUrl = shareLink
    ? (shareLink.url?.startsWith('http') ? shareLink.url : `${window.location.origin}${shareLink.path}`)
    : '';

  const whatsappHref = shareUrl
    ? `https://wa.me/?text=${encodeURIComponent(
        `${day?.staff?.name || 'Your'} daily schedule — open this private link on your phone: ${shareUrl}`
      )}`
    : '#';

  return (
    <div className="min-h-screen bg-slate-50 pb-28 print:bg-white" data-testid="staff-daily-schedule-page">
      <div className="mx-auto max-w-4xl px-4 pt-6">
        <button
          onClick={() => navigate('/provider-dashboard')}
          data-testid="schedule-back-btn"
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 print:hidden"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t('schedule.backToDashboard', 'Back to dashboard')}
        </button>

        <header className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">{t('schedule.dailySchedule', 'Daily schedule')}</p>
              <h1 className="mt-1 truncate text-2xl font-bold text-slate-900" data-testid="schedule-staff-name">
                {day?.staff?.name || t('schedule.dailySchedule', 'Daily schedule')}
              </h1>
              <p className="text-sm text-slate-500">
                {day?.staff?.role}
                {day?.provider?.name ? ` · ${day.provider.name}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              <button
                onClick={() => navigate(`/provider/schedule?date=${date}`)}
                data-testid="schedule-board-view-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200"
              >
                <LayoutGrid className="h-3.5 w-3.5" /> {t('schedule.dayBoard', 'Day board')}
              </button>
              <button
                onClick={() => setShareOpen((v) => !v)}
                data-testid="schedule-share-btn"
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold ${
                  shareOpen ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Link2 className="h-3.5 w-3.5" /> Share link
              </button>
              <button
                onClick={() => fetchDay(true)}
                data-testid="schedule-refresh-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200"
              >
                <RefreshCw className="h-3.5 w-3.5" /> {t('schedule.refresh', 'Refresh')}
              </button>
              <button
                onClick={() => window.print()}
                data-testid="schedule-print-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200"
              >
                <Printer className="h-3.5 w-3.5" /> {t('schedule.print', 'Print')}
              </button>
            </div>
          </div>

          {/* Private share link for this staff member */}
          {shareOpen && (
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 print:hidden" data-testid="schedule-share-panel">
              <p className="text-sm font-bold text-slate-900">
                Private link for {day?.staff?.name || 'this staff member'}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                They can open it on their phone to see only their own day. No login, no prices, no phone numbers.
              </p>

              {shareLink ? (
                <>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input
                      readOnly
                      value={shareUrl}
                      onFocus={(e) => e.target.select()}
                      data-testid="schedule-share-url"
                      className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-600"
                    />
                    <button
                      onClick={copyShareLink}
                      data-testid="schedule-share-copy-btn"
                      className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-800"
                    >
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="schedule-share-whatsapp-btn"
                      className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700"
                    >
                      Send on WhatsApp
                    </a>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button
                      onClick={() => createShareLink(true)}
                      disabled={shareBusy}
                      data-testid="schedule-share-rotate-btn"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-50"
                    >
                      <RotateCcw className="h-3.5 w-3.5" /> Replace with a new link
                    </button>
                    <button
                      onClick={revokeShareLink}
                      disabled={shareBusy}
                      data-testid="schedule-share-revoke-btn"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Turn off this link
                    </button>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => createShareLink(false)}
                  disabled={shareBusy}
                  data-testid="schedule-share-create-btn"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  <Link2 className="h-3.5 w-3.5" /> {shareBusy ? 'Creating…' : 'Create private link'}
                </button>
              )}

              {shareError && (
                <p className="mt-3 text-xs font-semibold text-rose-600" data-testid="schedule-share-error">
                  {shareError}
                </p>
              )}
            </div>
          )}

          {/* Date controls */}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              onClick={() => shiftDate(-1)}
              data-testid="schedule-prev-day-btn"
              className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
            >
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
            </button>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value || todayISO())}
              data-testid="schedule-date-picker"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-800"
            />
            <button
              onClick={() => shiftDate(1)}
              data-testid="schedule-next-day-btn"
              className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
            >
              <ChevronRight className="h-4 w-4 rtl:rotate-180" />
            </button>
            {date !== todayISO() && (
              <button
                onClick={() => setDate(todayISO())}
                data-testid="schedule-today-btn"
                className="rounded-full bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-700"
              >
                {t('schedule.today', 'Today')}
              </button>
            )}
            {day?.is_today && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700" data-testid="schedule-today-badge">
                <Sparkles className="h-3.5 w-3.5" /> {t('schedule.today', 'Today')}
              </span>
            )}
          </div>

          {/* Staff switcher */}
          {staffList.length > 0 && (
            <div className="mt-4 flex gap-2 overflow-x-auto pb-1 pretty-scroll" data-testid="schedule-staff-switcher">
              {staffList.map((s) => (
                <button
                  key={s.id}
                  onClick={() => navigate(`/provider/schedule/list/${s.id}?date=${date}`)}
                  data-testid={`schedule-staff-chip-${s.id}`}
                  className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                    s.id === staffId
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {s.name}
                  <span className={`rounded-full px-1.5 text-[11px] font-bold ${
                    s.id === staffId ? 'bg-white/20' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {s.is_day_off ? t('schedule.off', 'Off') : s.bookings}
                  </span>
                </button>
              ))}
            </div>
          )}
        </header>

        <ScheduleDayBody day={day} loading={loading} error={error} newIds={newIds} />

        {lastUpdated && (
          <p className="mt-6 text-center text-xs text-slate-400 print:hidden" data-testid="schedule-last-updated">
            {stale ? (
              <span className="font-semibold text-amber-600" data-testid="schedule-offline-hint">
                {t('schedule.offlineHint', 'Can’t reach the server — showing the {{time}} version', { time: lastUpdated.toLocaleTimeString() })}
              </span>
            ) : (
              <>{t('schedule.updated', 'Updated {{time}} · refreshes automatically', { time: lastUpdated.toLocaleTimeString() })}</>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default StaffDailySchedule;
